import { CalendarDays, Check, Clock3, Link2, MoreHorizontal, Pencil, Send, Trash2, Undo2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { confirmModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { fmtDate } from '../shared/format'
import { useProject } from './api'
import { PriorityBadge, TaskStatusBadge } from './components/StatusBadges'
import { TaskAssignmentPanel } from './components/assignment/TaskAssignmentPanel'
import { TaskActivity } from './components/task/TaskActivity'
import { TaskChecklist } from './components/task/TaskChecklist'
import { TaskFormModal } from './components/task/TaskFormModal'
import { TaskInfoCard } from './components/task/TaskInfoCard'
import { TaskRelations } from './components/task/TaskRelations'
import { useTaskDelete } from './components/task/useTaskDelete'
import { PROJECT_WORK_PERM } from './permissions'
import { useMoveTask } from './taskApi'
import { isOverdue } from './taskFilters'
import { TASK_STATUSES, type Project, type Task, type TaskStatus } from './types'
import { toastError } from './ui'
import { TASK_STATUS_DOT } from './visuals'

export function TaskDetailPage() {
  const { t } = useTranslation()
  const { projectId = '', taskId = '' } = useParams()
  const project = useProject(projectId)
  if (project.isError) return <ErrorState error={project.error} onRetry={() => void project.refetch()} />
  if (!project.data) return <Skeleton className="h-[32rem] rounded-xl" />
  const task = project.data.tasks.find((x) => x.id === taskId)
  if (!task) return <EmptyState title={t('projects.taskDetail.notFound')} />
  return <TaskDetail project={project.data} task={task} />
}

function TaskDetail({ project, task }: { project: Project; task: Task }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const navigate = useNavigate()
  const move = useMoveTask(project.id)
  const onDelete = useTaskDelete(project)
  const [editing, setEditing] = useState(false)
  const canWrite = can(PROJECT_WORK_PERM)
  const installation = project.installations.find((i) => i.id === task.installationId)

  const transition = async (status: TaskStatus, key: 'submit' | 'approve' | 'reject') => {
    const ok = await confirmModal({
      title: t(`projects.taskDetail.${key}Title`),
      description: t(`projects.taskDetail.${key}Body`, { key: task.key, title: task.title }),
      confirmLabel: t(`projects.taskDetail.${key}`),
      destructive: key === 'reject',
    })
    if (!ok) return
    move.mutate({ taskId: task.id, status }, { onSuccess: () => toast.success(t('projects.taskDetail.statusChanged', { status: t(`projects.taskStatus.${status}`) })), onError: toastError })
  }

  const copyLink = () => {
    void navigator.clipboard
      .writeText(window.location.href)
      .then(() => toast.success(t('projects.taskDetail.linkCopied')))
      .catch(() => toast.error(t('errors.generic')))
  }

  const parent = task.parentId ? project.tasks.find((x) => x.id === task.parentId) : undefined
  const backTo = parent ? `/projects/${project.id}/tasks/${parent.id}` : `/projects/${project.id}/kanban`

  const actions = canWrite ? (
    <>
      {task.status === 'approval' ? (
        <>
          <Button onClick={() => void transition('done', 'approve')}>
            <Check />
            {t('projects.taskDetail.approve')}
          </Button>
          <Button variant="outline" onClick={() => void transition('in_progress', 'reject')}>
            <Undo2 />
            {t('projects.taskDetail.reject')}
          </Button>
        </>
      ) : task.status !== 'done' ? (
        <Button onClick={() => void transition('approval', 'submit')}>
          <Send />
          {t('projects.taskDetail.submit')}
        </Button>
      ) : null}
      <Button variant="outline" onClick={() => setEditing(true)}>
        <Pencil />
        {t('common.edit')}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" aria-label={t('projects.moreActions')}>
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={copyLink}>
            <Link2 />
            {t('projects.taskDetail.copyLink')}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-destructive [&_svg]:text-destructive"
            onSelect={() => void onDelete(task).then((ok) => ok && navigate(backTo, { replace: true }))}
          >
            <Trash2 />
            {t('common.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  ) : null

  return (
    <>
      <PageHeader
        backTo={backTo}
        eyebrow={
          <span className="flex flex-wrap items-center gap-1.5">
            <Badge tone="primary" className="rounded-md">{project.name}</Badge>
            {installation ? <Badge tone="success" className="rounded-md">{installation.customer}</Badge> : null}
            <Badge tone="info" className="rounded-md">{t(`projects.taskType.${task.type}`)}</Badge>
            {parent ? <Badge tone="outline" className="rounded-md">{t('projects.taskDetail.subtaskOf', { key: parent.key })}</Badge> : null}
          </span>
        }
        title={
          <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <span className="font-mono text-primary">{task.key}</span>
            <span className="break-words">{task.title}</span>
            <Button size="icon" variant="ghost" className="size-7 text-muted-foreground" onClick={copyLink} aria-label={t('projects.taskDetail.copyLink')}>
              <Link2 />
            </Button>
          </span>
        }
        description={
          <span className="mt-1 flex flex-wrap items-center gap-3">
            {canWrite ? (
              <Select
                size="sm"
                className="w-40"
                value={task.status}
                aria-label={t('projects.tasks.fields.status')}
                onChange={(status) => move.mutate({ taskId: task.id, status }, { onError: toastError })}
                options={TASK_STATUSES.map((s) => ({ value: s, label: t(`projects.taskStatus.${s}`), icon: <span className={cn('size-2 rounded-full', TASK_STATUS_DOT[s])} /> }))}
              />
            ) : (
              <TaskStatusBadge status={task.status} />
            )}
            <PriorityBadge priority={task.priority} />
            <span className={cn('inline-flex items-center gap-1.5 tabular-nums', isOverdue(task) && 'font-medium text-destructive')}>
              <CalendarDays className="size-4" aria-hidden />
              {fmtDate(task.dueDate)}
            </span>
            <span className="inline-flex items-center gap-1.5 tabular-nums" title={t('projects.taskDetail.timeHint')}>
              <Clock3 className="size-4" aria-hidden />
              {t('projects.taskDetail.time', { estimate: task.estimateHours, spent: task.spentHours })}
            </span>
          </span>
        }
        actions={actions}
      />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="min-w-0 space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <section className="rounded-xl border bg-card shadow-card">
              <h2 className="border-b px-4 py-3 font-semibold">{t('projects.tasks.fields.description')}</h2>
              <p className="whitespace-pre-wrap break-words p-4 text-sm leading-relaxed text-muted-foreground">{task.description || t('projects.taskDetail.noDescription')}</p>
            </section>
            <TaskChecklist project={project} task={task} canWrite={canWrite} />
          </div>
          <TaskAssignmentPanel project={project} task={task} canWrite={canWrite} />
          <TaskRelations project={project} task={task} canWrite={canWrite} />
          <TaskActivity project={project} task={task} canWrite={canWrite} />
        </div>
        <TaskInfoCard project={project} task={task} />
      </div>

      <TaskFormModal open={editing} project={project} task={task} onOpenChange={setEditing} />
    </>
  )
}
